/**
 * Restores per-canvas working state on tab switch: the floating chat (open +
 * active session) and the right detail pane (route). Viewport is handled by
 * `useCanvasViewportStorage`, right-pane scroll by `useRightPaneScrollRestore`.
 *
 * Mounted inside the per-canvas remount boundary (CanvasPage), so its one-shot
 * restores re-arm on every switch. Chat lives inside the boundary (restored via
 * the chat context); the right pane lives outside it in React Router
 * `location.state` (restored via `appNavigate`).
 */

import { useCanvasFlowSessions } from "@/api/canvas";
import { useAppNavigate } from "@/hooks/pane-routing";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import type { LocationState } from "@/types/pane-routing";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import {
  readCanvasSession,
  unsuppressCanvasSession,
  writeCanvasSessionChat,
  writeCanvasSessionRightPane,
} from "./canvasSessionStorage";

export function useCanvasSessionRestore() {
  const { canvasId } = useCanvasRoute();
  const { open, currentSessionId, openSession } = useCanvasFloatingChat();
  const { data: sessions } = useCanvasFlowSessions(canvasId);
  const sessionsLoaded = sessions !== undefined;

  const location = useLocation();
  const appNavigate = useAppNavigate();
  const md = useBreakpoint("md");
  const currentRightPane =
    (location.state as LocationState | null)?.rightPane ?? null;

  const saved = useMemo(
    () => (canvasId ? readCanvasSession(canvasId) : null),
    [canvasId],
  );

  // A freshly (re)opened canvas should persist again, even if a prior close in
  // this session suppressed its snapshot.
  useEffect(() => {
    if (canvasId) unsuppressCanvasSession(canvasId);
  }, [canvasId]);

  // --- Chat: restore once after sessions hydrate, then capture changes ---
  // Gate capture behind `chatRestored` so the initial closed state doesn't
  // overwrite the saved snapshot before we get a chance to restore it.
  const [chatRestored, setChatRestored] = useState(false);

  useEffect(() => {
    if (!canvasId || chatRestored || !sessionsLoaded) return;
    const chat = saved?.chat;
    if (
      chat?.open &&
      chat.sessionId &&
      sessions?.some((s) => s.id === chat.sessionId)
    ) {
      openSession(chat.sessionId);
    }
    setChatRestored(true);
  }, [canvasId, chatRestored, sessionsLoaded, saved, sessions, openSession]);

  useEffect(() => {
    if (!canvasId || !chatRestored) return;
    writeCanvasSessionChat(canvasId, { open, sessionId: currentSessionId });
  }, [canvasId, chatRestored, open, currentSessionId]);

  // --- Right pane: restore once (desktop only), then capture changes ---
  // `rightPaneRestored` keeps restore one-shot (so its own navigation can't
  // re-trigger it). `restorePending` makes the capture effect skip the
  // pre-restore transient — otherwise it would persist the still-empty pane and
  // clobber the saved snapshot before the restore navigation lands. A stale doc
  // that no longer resolves is handled by the pane component (renders nothing).
  const rightPaneRestored = useRef(false);
  const restorePending = useRef(false);

  useEffect(() => {
    if (!canvasId || rightPaneRestored.current) return;
    rightPaneRestored.current = true;
    // On mobile `appNavigate` does a full-page navigation rather than opening an
    // overlay pane — too jarring to trigger automatically on canvas open.
    if (!md) return;
    const savedPane = saved?.rightPane;
    if (savedPane?.pathname && !currentRightPane?.pathname) {
      restorePending.current = true;
      appNavigate({
        pane: "rightPane",
        to: savedPane.pathname,
        options: {
          replace: true,
          preventScrollReset: true,
          state: savedPane.state,
        },
      });
    }
    // Run once per canvas mount; `saved`/`currentRightPane` are read at first run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasId, md]);

  useEffect(() => {
    if (!canvasId || !rightPaneRestored.current) return;
    // Skip the pre-restore transient (empty pane before the restore nav lands).
    if (restorePending.current && !currentRightPane?.pathname) return;
    restorePending.current = false;
    writeCanvasSessionRightPane(
      canvasId,
      currentRightPane?.pathname
        ? {
            pathname: currentRightPane.pathname,
            search: currentRightPane.search,
            hash: currentRightPane.hash,
            state: currentRightPane.state,
          }
        : null,
    );
  }, [canvasId, currentRightPane]);
}
